import { NumberInput } from "../../shared/NumberInput";
import { Slider } from "../../shared/Slider";
import { fmtEUR } from "../../lib/calculations";
import { DEFAULT_VACANCE, type RentalType, type SimulationInput } from "../../lib/types";

const LOCATION_OPTIONS: { value: RentalType; title: string; subtitle: string }[] = [
  { value: "longue", title: "Longue durée", subtitle: "Bail 1 an +" },
  { value: "moyenne", title: "Moyenne durée", subtitle: "Bail mobilité 1-10 mois" },
  { value: "courte", title: "Courte durée", subtitle: "Airbnb, saisonnier" },
];

interface Props {
  input: SimulationInput;
  update: <K extends keyof SimulationInput>(k: K, v: SimulationInput[K]) => void;
  revenusAnnuels: number;
  revenusMensuels: number;
}

export function StepRevenus({ input, update, revenusAnnuels, revenusMensuels }: Props) {
  return (
    <div className="space-y-5">
      <header>
        <p className="text-sm font-medium text-primary">Étape 2 / 4</p>
        <h2 className="mt-1 text-2xl font-bold text-foreground">Combien ça rapporte ?</h2>
      </header>

      <NumberInput
        label="Loyer mensuel hors charges"
        value={input.loyerMensuel}
        onValueChange={(n) => update("loyerMensuel", n)}
        placeholder="ex: 1 200"
        required
      />

      <div className="space-y-1.5">
        <label className="block text-sm font-medium text-foreground">Type de location</label>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Type de location">
          {LOCATION_OPTIONS.map((opt) => {
            const active = input.typeLocation === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => {
                  update("typeLocation", opt.value);
                  update("tauxVacance", DEFAULT_VACANCE[opt.value]);
                }}
                className={`flex flex-col items-center justify-center gap-0.5 rounded-xl border px-3 py-3 text-center transition-all ${
                  active
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border bg-background hover:border-primary/40 hover:bg-muted/30"
                }`}
              >
                <span className={`text-sm font-semibold ${active ? "text-primary" : "text-foreground"}`}>
                  {opt.title}
                </span>
                <span className="text-[11px] leading-tight text-muted-foreground">
                  {opt.subtitle}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Slider
        label="Taux de vacance estimé"
        value={input.tauxVacance}
        onChange={(n) => update("tauxVacance", n)}
        min={0}
        max={30}
        step={1}
        helper="Temps sans locataire dans l'année"
      />

      <div className="rounded-xl border border-success/30 bg-success/10 p-4 space-y-1" role="status">
        <p className="text-sm text-foreground">
          Revenus annuels nets de vacance :{" "}
          <strong className="tabular font-bold">{fmtEUR(revenusAnnuels)}</strong>
        </p>
        <p className="text-sm text-muted-foreground">
          Soit <strong className="tabular text-foreground">{fmtEUR(revenusMensuels)}</strong>/mois en moyenne
        </p>
      </div>
    </div>
  );
}
